import type { LucideIcon } from 'lucide-react';
import { ArrowDownRight, ArrowUpRight } from 'lucide-react';
import { cn } from '@/lib/cn';

export type KpiAccent = 'primary' | 'sky' | 'emerald' | 'violet' | 'amber' | 'rose';

const ACCENTS: Record<KpiAccent, { gradient: string; icon: string }> = {
  primary: {
    gradient: 'from-primary to-primary/60',
    icon: 'bg-primary/10 text-primary',
  },
  sky: {
    gradient: 'from-sky-400 to-cyan-300',
    icon: 'bg-sky-500/10 text-sky-500',
  },
  emerald: {
    gradient: 'from-emerald-400 to-teal-300',
    icon: 'bg-emerald-500/10 text-emerald-500',
  },
  violet: {
    gradient: 'from-violet-400 to-fuchsia-300',
    icon: 'bg-violet-500/10 text-violet-500',
  },
  amber: {
    gradient: 'from-amber-400 to-orange-300',
    icon: 'bg-amber-500/10 text-amber-600',
  },
  rose: {
    gradient: 'from-rose-400 to-pink-300',
    icon: 'bg-rose-500/10 text-rose-500',
  },
};

export interface KpiTileProps {
  label: string;
  value: string | number;
  /** Percentage change, e.g. 12.5 (▲) or -4 (▼). */
  delta?: number;
  icon: LucideIcon;
  accent?: KpiAccent;
  /** Optional inline trailing element (e.g. a sparkline). */
  trailing?: React.ReactNode;
  loading?: boolean;
}

export function KpiTile({
  label,
  value,
  delta,
  icon: Icon,
  accent = 'primary',
  trailing,
  loading,
}: KpiTileProps): JSX.Element {
  const a = ACCENTS[accent];
  const showDelta = typeof delta === 'number' && Number.isFinite(delta);
  const positive = (delta ?? 0) >= 0;
  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
          {label}
        </span>
        <span
          className={cn(
            'flex h-8 w-8 items-center justify-center rounded-md',
            a.icon,
          )}
          aria-hidden
        >
          <Icon className="h-4 w-4" />
        </span>
      </div>
      <div className="flex items-end justify-between gap-2">
        <div
          className={cn(
            'bg-gradient-to-br bg-clip-text text-3xl font-semibold tabular-nums text-transparent',
            a.gradient,
          )}
        >
          {loading ? '—' : value}
        </div>
        {trailing ? <div className="h-8 w-20">{trailing}</div> : null}
      </div>
      {showDelta ? (
        <div
          className={cn(
            'inline-flex items-center gap-1 text-xs font-medium',
            positive ? 'text-emerald-500' : 'text-rose-500',
          )}
        >
          {positive ? (
            <ArrowUpRight className="h-3 w-3" />
          ) : (
            <ArrowDownRight className="h-3 w-3" />
          )}
          <span>{Math.abs(delta as number).toFixed(1)}%</span>
          <span className="text-muted-foreground">vs prev</span>
        </div>
      ) : null}
    </div>
  );
}
